import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  TeamAddForm,
  TeamAddInitialValues,
  TeamComponents,
  TeamAdditionalParams,
  TeamFormOptions,
  onTeamFormSaved,
  ITeamFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateTeam, PermissionEnum, } from 'atlas-shared';
import { createTeam, fetchTeam, getTeamCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useUsers, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateTeam> {}

const TeamFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const users = useUsers();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateTeam>>();

  const param_props: ITeamFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    users: users.users,
  };
  const formLayout = useMemo(() => TeamAddForm(values, param_props, t), [values, organizations, users, t, abilities, ]);
  const options: IFormOptions = useMemo(() => TeamFormOptions(values || TeamAddInitialValues, param_props, t), [values, organizations, users, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateTeam, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateTeam) => save(() => createTeam(values).then(
    res => onSaved ? onSaved(res, navigate) : onTeamFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchTeam(+id)
        .then(({
          organization_id,
          title,
          __users,
        }) => {
          setCloned({
            organization_id,
            title,
            __users,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !users.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.Team, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={TeamComponents}
        additionalParams={TeamAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...TeamAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('TEAM')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const TeamFormAddView = React.memo(withAdd(TeamFormAdd, getTeamCreateSchema, TeamAddInitialValues));
